import React from "react";
import styled from 'styled-components';
import I10n from '../../../features/i10n';
import MsxIcons from '../../msx-icons';
import Scrollbars from 'react-custom-scrollbars';
import { front_base_url, configurationModuleType, clusterTerminationData } from '../../../constants';
import { formatLengthUnit } from '../../../utils/data-formatter';
import DimensionRule from '../../../components/dimension-rule';
import Device from '../../../models/Device';
import IOIsland from '../../../models/IOIsland';
import Configuration from '../../../models/Configuration';


const Container = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: white;
  color: #333333;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  color: #43CE5D;
  color: var(--se-primary, #43CE5D);
  border-bottom: 2px solid #43CE5D;
  border-bottom: 2px solid var(--se-primary, #43CE5D);
  margin: 0px 20px;
  padding: 10px 0px;
`;

const Title = styled.span`
  font-weight: bold;
  font-size: 20px;
`;

const CloseIcon = styled.span`
  display: inline-block;
  color: #43CE5D;
  color: var(--se-primary, #43CE5D);
  cursor: pointer;
`;

const Content = styled.div`
  flex: 1 1 0px;
  display: flex;
  margin: 10px 20px;
  overflow: hidden;
  background-color: #fafafa;
  box-shadow: inset 0 0 15px 1px rgba(0,0,0,0.1);
`;

const InlineRow = styled.div`
  position: relative;
  display: inline-block;
  ${props => props.paddingLeft ? `padding-left: ${props.paddingLeft}px;` : ''}
`;

const ScrollbarContent = styled.div`
  display: inline-block;
  margin: ${props => props.showDimensions ? 30 : 15}px 15px 15px 15px;
`;

const GraphicalViewItem = styled.div`
  position: relative;
  height: 100%;
`;

const BlockWrapper = styled.div`
  display: inline-block;
  ${props => !props.isLast && `margin-right: 20px;`}
`;

const BlockWithDimension = styled.div`
  position: relative;
  display : inline-block;
`;

const BlockModulesWrapper = styled.div`
  display: inline-block;
`;

const ModuleWithBusWrapper = styled.div`
  display: inline-block;
`;

const ModuleItem = styled(GraphicalViewItem)`
  display: inline-block;
  height: ${props => props.height ? props.height : 160}px;
  z-index: 1;
  position: relative;
`;

const ModuleInput = styled.input`
  position: absolute;
  opacity: 0.00001;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  z-index: -1;
`;

const DeviceImageWrapper = styled.label`
  display: block;
  position: relative;

  :after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    box-sizing: border-box;

    ${props => props.selected && `
      border: 4px solid #42b4e6;
      border-radius: 2px;
      background-color: rgba(66, 180, 230, 0.25);
    `}
  }
`;

const DeviceImage = styled.img`
  display: block;
  height: ${props => `${props.height}px`};
  width: ${props => `${props.width}px`};
  font-size: 8px;
  writing-mode: vertical-lr;
  text-orientation: upright;
  text-align: center;
`;
const SlottedDeviceImage = styled(DeviceImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: ${props => props.width}px;
  height: ${props => props.height}px;
`;

const EdgeWireStart = styled.div`
  position: absolute;
  width: ${props => props.width}px;
  height: 100px;
  margin-left: 50%;
  bottom: -15px;
  border-left: 5px solid #42B4E6;
  border-left: 5px solid var(--se-secondary, #42B4E6);
  border-bottom: 5px solid #42B4E6;
  border-bottom: 5px solid var(--se-secondary, #42B4E6);
`;

const EdgeWireMiddleWrapper = styled.div`
  position: absolute;
  width: ${props => props.width}px;
  bottom: -15px;
`;

const EdgeWireLineH = styled.div`
  width: ${props => props.width}px;
  height: 15px;
  border-bottom: 5px solid #42B4E6;
  border-bottom: 5px solid var(--se-secondary, #42B4E6);
`;

const EdgeWireEnd = styled.div`
  position: absolute;
  width: 50%;
  height: 100px;
  border-right: 5px solid #42B4E6;
  border-right: 5px solid var(--se-secondary, #42B4E6);
  border-bottom: 5px solid #42B4E6;
  border-bottom: 5px solid var(--se-secondary, #42B4E6);
  bottom: -15px;
`;

const ModuleAndWiringWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const protocolColors = {
  "Modbus SL": "black",
  "Modbus TCP": "#3DCD58",
  "CANopen": "#7A30A0",
  "EtherNet/IP": "#3DCD58",
  "Sercos": "#B10043"
};

class ModalPreviewEdgeConfiguration extends React.PureComponent {

  constructor(props) {
    super(props);
    this.state = {
      showDimensions: true,
    }
  }

  // On mount and if there is a module hightlighted, scroll to this module
  componentDidMount() {
    if (this._highlightedModuleElement && this._scrollElement) {

      this._scrollElement.view.scrollTop = Math.max(0, (this._highlightedModuleElement.getBoundingClientRect().y - 100.0));
      this._scrollElement.view.scrollLeft = Math.max(0, (this._highlightedModuleElement.getBoundingClientRect().x - 70.0));
    }
  }

  computeModuleHeight = (realHeight, maxModuleHeight) => {
    const heightRatio = Math.ceil(100 * realHeight / maxModuleHeight) / 100;
    return 160 * (heightRatio ? heightRatio : 1);
  }

  computeModuleWidth = (realWidth, realHeight, newHeight) => {
    const aspectRatio = realWidth / realHeight;
    return newHeight * (aspectRatio ? aspectRatio.toFixed(2) : 1);
  }

  shouldModuleBeHighlighted = (moduleSlot, moduleType, moduleIslandSlot) => {
    let result = false;

    if (this.props.moduleToHighlightProps != null && this.props.moduleToHighlightProps.moduleSlot != null && this.props.moduleToHighlightProps.moduleType != null && this.props.moduleToHighlightProps.moduleIslandSlot != null) {
      if (this.props.moduleToHighlightProps.moduleSlot === moduleSlot && this.props.moduleToHighlightProps.moduleType === moduleType && this.props.moduleToHighlightProps.moduleIslandSlot === moduleIslandSlot) {
        result = true;
      }
    }

    return result;
  }

  setHighlightedModuleRef = element => {
    this._highlightedModuleElement = element;
  };

  renderEdgeWiring = (idxInBlock, blockIdx, nbBlocks, imageWidth) => {
    const isLastBlock = ((blockIdx + 1) === nbBlocks);
    const isFirstBlock = blockIdx === 0;

    return (
      <>
        {idxInBlock === 0 && nbBlocks > 1 &&
          <>
            {isLastBlock && <EdgeWireEnd>
            </EdgeWireEnd>}
            {!isLastBlock && <InlineRow>
              <EdgeWireStart width={imageWidth / 2.0 + 20}>
              </EdgeWireStart>
            </InlineRow>}
          </>
        }
        {!isLastBlock && (idxInBlock > 0 || !isFirstBlock) &&
          <EdgeWireMiddleWrapper width={imageWidth + 20}>
            <EdgeWireLineH width={imageWidth + 20}></EdgeWireLineH>
          </EdgeWireMiddleWrapper>
        }
      </>
    );
  }


  renderModule = (device, maxModuleHeight, maxIslandHeight, idxInBlock, blockIdx, nbBlocks, moduleProps = {}, selected = false) => {
    const slot = device.slot;
    const moduleType = device.moduleType;
    const realHeight = device.height ? device.height : 0;
    const realWidth = device.width ? device.width : 0;
    const imageHeight = this.computeModuleHeight(realHeight, maxModuleHeight);
    const imageWidth = this.computeModuleWidth(realWidth, realHeight, imageHeight);
    const slottedDevicesImages = device.getSlottedDevicesFrontImageLinks();

    const { 'item': itemProps = {}, 'image': imageProps = {} } = moduleProps;
    return (
      <ModuleAndWiringWrapper>
        <ModuleItem height={maxIslandHeight} key={`${device.id}_${slot}`} {...itemProps}>
          <ModuleInput
            id={`${device.id}_${slot}`}
            type="radio" name="config_module"
            className={`${device.validator.valid ? 'valid' : 'invalid'}`}
            checked={moduleType === this.state.typeModuleSelected && slot === this.state.slotModuleSelected}
            onChange={() => { }}
          ></ModuleInput>
          <DeviceImageWrapper ref={(elt) => { if (selected) { this.setHighlightedModuleRef(elt); } }} htmlFor={`${device.id}_${slot}`} {...imageProps} selected={selected}>
            <DeviceImage
              src={`${front_base_url}/images/small-devices-images/${device.id}_h320px.png`}
              alt={``}
              onError={(e) => { e.target.onerror = null; e.target.src = device.sidePicture }}
              height={imageHeight}
              width={imageWidth}
            >
            </DeviceImage>
            {!(configurationModuleType.head_module === moduleType) && slottedDevicesImages.map((imgObj, i) => {
              return (
                <SlottedDeviceImage
                  key={i}
                  src={`${front_base_url}/images/small-devices-images/${imgObj.src}_h320px.png`}
                  height={imageHeight}
                  width={imageWidth}
                >
                </SlottedDeviceImage>
              );
            })}
          </DeviceImageWrapper>
        </ModuleItem>
        {this.renderEdgeWiring(idxInBlock, blockIdx, nbBlocks, imageWidth)}
      </ModuleAndWiringWrapper>
    );
  };

  renderHeadModule = (configuration, maxModuleHeight, islandMaxHeight, islandSlot, idxInBlock, blockIdx, nbBlocks) => {
    const device = configuration.headModule;

    const moduleProps = { 'item': {}, 'image': {} };
    return (
      <>
        {this.renderModule(device, maxModuleHeight, islandMaxHeight, idxInBlock, blockIdx, nbBlocks, moduleProps, this.shouldModuleBeHighlighted(device.slot, device.moduleType, islandSlot))}
      </>
    );
  }

  renderTermination = (nbModulesInBlock, maxModuleHeight, blockIdx, nbBlocks) => {
    const device = new Device(clusterTerminationData);
    const realHeight = device.height ? device.height : 0;
    const realWidth = device.width ? device.width : 0;
    const imageHeight = this.computeModuleHeight(realHeight, maxModuleHeight);
    const imageWidth = this.computeModuleWidth(realWidth, realHeight, imageHeight);

    return (
      <ModuleWithBusWrapper>
        <InlineRow paddingLeft={0}>
          <ModuleAndWiringWrapper>
            <ModuleItem>
                <DeviceImage
                  src={`${front_base_url}/images/small-devices-images/${device.id}_h320px.png`}
                  alt={``}
                  height={imageHeight}
                  width={imageWidth}
                >
                </DeviceImage>
            </ModuleItem>
            {this.renderEdgeWiring(nbModulesInBlock, blockIdx, nbBlocks, imageWidth)}
          </ModuleAndWiringWrapper>
        </InlineRow>
      </ModuleWithBusWrapper>
    );
  }

  renderByBlocks = (maxModuleHeight, islandMaxHeight, configuration, rulePosition, islandSlot) => {
    const blocks = configuration.blocks();
    return (
      blocks.map((block, blockIdx) => {

        const maxBlockHeight = this.computeModuleHeight(block.maxModuleHeight(), maxModuleHeight);
        return (
          <BlockWrapper key={blockIdx} isLast={(blockIdx + 1) === blocks.length}>
            <BlockWithDimension>
              {this.state.showDimensions && block.hasWidth() && (
                <DimensionRule
                  text={formatLengthUnit(block.width())}
                  orientation={'horizontal'}
                  position={rulePosition}
                  offsetTop={-20}
                  offsetBottom={islandMaxHeight - maxBlockHeight - 5}
                ></DimensionRule>
              )}
              <BlockModulesWrapper>
                {block.modules.map((module, idx) => {
                  return (
                    <ModuleWithBusWrapper key={idx}>
                      <InlineRow paddingLeft={0}>
                        {
                          module.moduleType === configurationModuleType.head_module ?
                            this.renderHeadModule(configuration, maxModuleHeight, islandMaxHeight, islandSlot, idx, blockIdx, blocks.length)
                            :
                            this.renderModule(module, maxModuleHeight, islandMaxHeight, idx, blockIdx, blocks.length, {}, this.shouldModuleBeHighlighted(module.slot, module.moduleType, islandSlot))
                        }
                      </InlineRow>
                    </ModuleWithBusWrapper>
                  );
                })}
                {this.renderTermination(block.modules.length, maxModuleHeight, blockIdx, blocks.length)}
              </BlockModulesWrapper>
            </BlockWithDimension>
          </BlockWrapper>
        );
      })
    );
  }

  islandsConnectedToModuleWithProtocol = (module, configuration, protocolName) => {
    const islands = [];
    const configurationPLC = configuration.headModule;
    const islandsConnectedWithProtocol =
      configuration.getIslandsConnectedWithProtocol(protocolName);

    if (configurationModuleType.head_module === module.moduleType) {
      islands.push(...islandsConnectedWithProtocol);
    } else {
      islandsConnectedWithProtocol.forEach(island => {
        if (configurationPLC.masterProtocols.filter(islandProtocol => islandProtocol.name !== protocolName).length > 0) {
          islands.push(island);
        }
      });
    }
    return islands;
  }

  buildProtocolGroups = (configuration, maxModuleHeight) => {
    const protocolWithIslandsAndOffset = {};
    let currentOffset = 0;
    configuration.blocks().forEach((block, bidx) => {
      block.modules.forEach((mod) => {
        // Offset for cable.
        if (bidx > 0) {
          currentOffset += 5;
        }
        const modProtocolGroups = {};
        const graphicalModuleHeight = this.computeModuleHeight(mod.height, maxModuleHeight);
        const graphicalModuleWidth = (mod.width * this.computeModuleHeight(mod.height, maxModuleHeight)) / mod.height;
        mod.masterProtocols.forEach((protocol) => {
          const protocolName = protocol.name;
          this.islandsConnectedToModuleWithProtocol(mod, configuration, protocolName).forEach((island) => {
            if (modProtocolGroups[protocolName]) {
              modProtocolGroups[protocolName].islands.push(island);
            } else {
              modProtocolGroups[protocolName] = { islands: [island], protocolName: protocolName };
            }
          });
        });

        const nbProtocolGroups = Object.entries(modProtocolGroups).length;
        // Affect protocol groups offset.
        Object.entries(modProtocolGroups).forEach(([, modProtocolGroup], index) => {
          protocolWithIslandsAndOffset[modProtocolGroup.protocolName] = modProtocolGroup;
          protocolWithIslandsAndOffset[modProtocolGroup.protocolName].offset = currentOffset + ((graphicalModuleWidth / (nbProtocolGroups + 2)) * (index + 1));
        });

        currentOffset += (mod.width * graphicalModuleHeight) / mod.height;
      });

    });

    if (configuration.slc && protocolWithIslandsAndOffset['Sercos']) {
      protocolWithIslandsAndOffset['Sercos'].islands.push(
        new IOIsland(
          {
            configuration: new Configuration({
              name: I10n.get('saved_configuration.export.pdf_architecture.slc'),
              headModule: new Device({
                id: configuration.slc.module.id,
                height: 40,
                width: 50
              })
            })
          }
        )
      );
    }

    return Object.values(protocolWithIslandsAndOffset).sort((a, b) => b.offset - a.offset);
  }

  buildProtocolLines = (protocolGroups) => {
    const protocolLines = [];
    protocolGroups.forEach((protocolGroup) => {
      let currentLine = { protocolName: protocolGroup.protocolName, offset: protocolGroup.offset, islands: [] };
      protocolGroup.islands.forEach((island) => {
        currentLine.islands.push(island);
      });
      protocolLines.push(currentLine);
    });
    return protocolLines;
  }

  generateProtocolLines = (configuration, maxModuleHeight) => {
    const protocolGroups = this.buildProtocolGroups(configuration, maxModuleHeight);
    const protocolLines = this.buildProtocolLines(protocolGroups);
    let currentView;
    while (protocolLines.length > 0) {
      const currentLine = protocolLines.shift();
      let shouldRenderBorder = true;
      let previousOffset = 0;
      if (protocolLines.length > 0) {
        previousOffset = protocolLines[0].offset;
        shouldRenderBorder = previousOffset !== currentLine.offset;
      }
      currentView = (
        <div style={{ marginLeft: currentLine.offset - previousOffset, marginBottom: 5 }}>
          <div style={{ display: "flex", flexDirection: "row", minHeight: 18 }}>
            <div style={{ borderLeft: shouldRenderBorder ? `5px solid ${protocolColors[currentLine.protocolName]}` : `0px solid ${protocolColors[currentLine.protocolName]}` }}></div>
            {currentView}
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ paddingLeft: 5, borderLeft: shouldRenderBorder ? `5px solid ${protocolColors[currentLine.protocolName]}` : `0px solid ${protocolColors[currentLine.protocolName]}`, color: protocolColors[currentLine.protocolName], fontSize: 12, }}>{currentLine.protocolName}</span>
            <div style={{ display: "flex", flexDirection: "row", marginBottom: 20 }}>
              {currentLine.islands.map((island, idx) => {
                return (
                  <div key={idx} style={{ display: "flex", flexDirection: "column" }}>

                    <div style={{ borderTop: `4px solid ${protocolColors[currentLine.protocolName]}` }}></div>
                    <div style={{ paddingLeft: 20 }}>
                      {this.generateGraphicalConfiguration(island.configuration, currentLine.protocolName, maxModuleHeight, island.slot, 'bottom')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>);
    }

    return currentView;
  }

  generateGraphicalConfiguration = (configuration, protocolName, maxModuleHeight, islandSlot, rulePosition = 'bottom') => {
    const borderWidth = protocolName ? 5 : 0;
    const borderColor = protocolName ? protocolColors[protocolName] : 'black';
    const islandHeight = this.computeModuleHeight(configuration.maxModuleHeight(), maxModuleHeight);
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 14, marginLeft: 5, paddingLeft: 5, borderLeft: `${borderWidth}px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
          {configuration.name}
        </span>
        <div style={{ display: "flex", flexDirection: "row", whiteSpace: "nowrap", height: islandHeight }}>
          {this.renderByBlocks(maxModuleHeight, islandHeight, configuration, rulePosition, islandSlot)}
        </div>
      </div>
    );
  }

  setScrollRef = element => {
    this._scrollElement = element;
  };

  render = () => {
    const maxModuleHeight = this.props.configuration.maxModuleHeightWithRemote();
    return (
      <Container>
        <Header>
          <Title>{I10n.get('screen.config_editor.preview')}</Title>
          <CloseIcon href="#" onClick={(e) => {
            e.preventDefault();
            this.props.closeModal();
          }}>
            <MsxIcons name="modal-close"></MsxIcons>
          </CloseIcon>
        </Header>
        <Content>
          <Scrollbars ref={this.setScrollRef}>
            <ScrollbarContent showDimensions={this.state.showDimensions}>
              {this.generateGraphicalConfiguration(this.props.configuration, null, maxModuleHeight, 0, 'top')}
              {this.generateProtocolLines(this.props.configuration, maxModuleHeight)}
            </ScrollbarContent>
          </Scrollbars>
        </Content>
      </Container>
    );
  }
}

export default ModalPreviewEdgeConfiguration;
